import React, { useState, useEffect } from "react";
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  ProjectStatusKey,
  projectStatusColors,
} from "../../utils/statusColors";
import { fromServerToLocal } from "../../utils/time";
import {
  isOfflineActionAvailable,
  getOfflineMessage,
} from "../../utils/offlineGuard";
import Notification from "../Common/Notification";
import { Project } from "../../types";
import { getCachedStatuses } from "../../storage/preloadData";
import { updateProject } from "../../services/projectService";
import StatusChangeModal from "../Modals/StatusChangeModal";

export interface ProjectCardProps {
  project: Project;
  getStatusConfig: (status: ProjectStatusKey) => {
    color: string;
    translation: string;
  };
  onStatusChange?: () => void;
  onStatusUpdateSuccess?: (message: string) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  getStatusConfig,
  onStatusChange,
  onStatusUpdateSuccess,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const statusConfig = getStatusConfig(project.status as ProjectStatusKey);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [offlineNotification, setOfflineNotification] = useState<string | null>(
    null,
  );
  const [isClickable, setIsClickable] = useState(true);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const dateFormat = i18n.language === "en_US" ? "MM/DD/YYYY" : "DD.MM.YYYY";

  const endLocal = project.dateEnd ? fromServerToLocal(project.dateEnd) : null;

  const formattedEnd =
    endLocal && dayjs(endLocal).isValid()
      ? dayjs(endLocal).format(dateFormat)
      : "-";

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const statuses = await getCachedStatuses();
        const projectStatuses = statuses.projects || [];
        if (projectStatuses.length > 0) {
          setAvailableStatuses(projectStatuses);
        } else {
          setAvailableStatuses([
            "Draft",
            "Active",
            "Closed",
            "Paused",
            "Canceled",
          ]);
        }
      } catch (err) {
        console.error("Failed to load project statuses:", err);
        setAvailableStatuses([
          "Draft",
          "Active",
          "Closed",
          "Paused",
          "Canceled",
        ]);
      }
    };
    loadStatuses();
  }, []);

  const statusColorsMap: Record<string, string> = Object.keys(
    projectStatusColors,
  ).reduce(
    (acc, key) => {
      acc[key] = projectStatusColors[key as ProjectStatusKey].color;
      return acc;
    },
    {} as Record<string, string>,
  );

  const handleStatusSave = async (newStatus: string) => {
    setIsLoading(true);
    try {
      await updateProject(project.id, { status: newStatus });
      setStatusModalOpen(false);
      if (onStatusUpdateSuccess) {
        onStatusUpdateSuccess(
          t("statusChangeModal.success") || "Статус успешно изменен",
        );
      }
      const { invalidateListCache } =
        await import("../../storage/cacheManager");
      await invalidateListCache("projects");
      if (onStatusChange) {
        onStatusChange();
      } else {
        window.location.reload();
      }
    } catch (err: any) {
      console.error("Failed to update project status:", err);
      setOfflineNotification(err.message || "Ошибка при обновлении статуса");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setStatusModalOpen(true);
  };

  const handleClick = async () => {
    const { available, reason } = await isOfflineActionAvailable("view", t);

    if (!available) {
      setOfflineNotification(reason || getOfflineMessage("view", t));
      setIsClickable(false);
      setTimeout(() => {
        setIsClickable(true);
      }, 3000);
      return;
    }

    navigate(`/project/${project.id}`);
  };

  const progress = project.cProgres !== null ? project.cProgres : 0;
  const progressText = `${t("projects.columnNames.progress")} - ${progress}%`;

  const getProgressColor = (value: number): string => {
    if (value <= 33) {
      return "#8B0000";
    } else if (value <= 66) {
      return "#FF8C00";
    } else {
      return "#228B22";
    }
  };

  const progressColor = getProgressColor(progress);

  const cardContent = (
    <CardContent sx={{ p: { xs: 1, sm: 1.5 }, position: "relative" }}>
      {isMobile && (
        <Chip
          label={statusConfig.translation}
          onClick={handleStatusClick}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: "100px",
            backgroundColor: statusConfig.color,
            color: "#fff",
            fontSize: "0.6rem",
            fontWeight: "bold",
            borderRadius: "24px",
            py: 0,
            px: 0,
            textAlign: "center",
            cursor: "pointer",
            "&:hover": {
              opacity: 0.8,
            },
          }}
        />
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Box sx={{ flex: { sm: "0 0 25%" }, maxWidth: { sm: "25%" } }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.9rem", sm: "1rem" },
              fontWeight: "medium",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              pr: { xs: 8, sm: 1 },
            }}
          >
            {project.name}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: { sm: "0 0 15%" },
            maxWidth: { sm: "15%" },
            textAlign: { sm: "center" },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "0.8rem", sm: "0.875rem" },
              color: progressColor,
              fontWeight: 500,
            }}
          >
            {progressText}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: { sm: "0 0 25%" },
            maxWidth: { sm: "25%" },
            textAlign: { sm: "center" },
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
          >
            {isMobile
              ? `${t("whenRange.to")} ${formattedEnd}`
              : `${t("whenRange.to")} ${formattedEnd}`}
          </Typography>
        </Box>

        <Box
          sx={{
            flex: { sm: "0 0 20%" },
            maxWidth: { sm: "20%" },
            textAlign: { sm: "center" },
            display: { xs: "none", sm: "flex" },
            justifyContent: "center",
          }}
        >
          <Chip
            label={statusConfig.translation}
            onClick={handleStatusClick}
            sx={{
              width: { sm: "140px" },
              backgroundColor: statusConfig.color,
              color: "#fff",
              borderRadius: "16px",
              py: 0.5,
              fontSize: { sm: "0.75rem" },
              fontWeight: "bold",
              textAlign: "center",
              cursor: "pointer",
              "&:hover": {
                opacity: 0.8,
              },
            }}
          />
        </Box>
      </Box>
    </CardContent>
  );

  const isOffline = !navigator.onLine;
  const offlineMessage = getOfflineMessage("view", t);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          mb: 1,
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          border: "none",
          opacity: isOffline && !isClickable ? 0.6 : 1,
          cursor: isOffline && !isClickable ? "not-allowed" : "pointer",
        }}
      >
        {isOffline && !isClickable ? (
          <Tooltip title={offlineMessage} arrow>
            <Box>{cardContent}</Box>
          </Tooltip>
        ) : (
          <CardActionArea onClick={handleClick} disabled={!isClickable}>
            {cardContent}
          </CardActionArea>
        )}
      </Card>
      <Notification
        open={!!offlineNotification}
        message={offlineNotification || ""}
        severity="warning"
        onClose={() => setOfflineNotification(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      />
      <StatusChangeModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onSave={handleStatusSave}
        currentStatus={project.status}
        availableStatuses={availableStatuses}
        statusType="project"
        statusColors={statusColorsMap}
        isLoading={isLoading}
      />
    </>
  );
};

export default ProjectCard;
